<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue';
import * as THREE from 'three';

const container = ref<HTMLElement | null>(null);
let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let time = 0;
let scrollY = 0;
let mouse = new THREE.Vector2(-9999, -9999);

const PLANE_COUNT = 6; // Increased from 4
const PARTICLE_COUNT_PER_PLANE = 4000;
const TRAIL_POINTS_PER_PLANE = 200;
const TOTAL_TRAIL_PARTICLES = PLANE_COUNT * TRAIL_POINTS_PER_PLANE;

interface PlaneInstance {
  points: THREE.Points;
  geometry: THREE.BufferGeometry;
  originalPositions: Float32Array;
  trailHistory: THREE.Vector3[];
  config: {
    size: number;
    speedScale: number;
    yPos: number;
    zPos: number;
    wobbleSpeed: number;
    startX: number;
    endX: number;
    delay: number;
  }
}

let planes: PlaneInstance[] = [];
let trailGeometry: THREE.BufferGeometry;
let trailPoints: THREE.Points;

onMounted(() => {
  if (!container.value) return;

  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.z = 20;

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(window.devicePixelRatio);
  container.value.appendChild(renderer.domElement);

  // Colors from BetPic Palette
  const colorSkySlate = new THREE.Color(0xBCCBDD);
  const colorCloudburst = new THREE.Color(0xE0E6ED);
  const colorPrimaryRed = new THREE.Color(0xE60000);

  // Airplane Template
  const vNose = new THREE.Vector3(0, 0, 2.5);
  const vWingLeft = new THREE.Vector3(-2, 0.4, -1.5);
  const vWingRight = new THREE.Vector3(2, 0.4, -1.5);
  const vSpineBack = new THREE.Vector3(0, 0.4, -1.5);
  const vKeelBack = new THREE.Vector3(0, -0.6, -1.2);

  const getRandomPointOnTriangle = (v1: THREE.Vector3, v2: THREE.Vector3, v3: THREE.Vector3) => {
    let a = Math.random();
    let b = Math.random();
    if (a + b > 1) { a = 1 - a; b = 1 - b; }
    const c = 1 - a - b;
    return new THREE.Vector3().addScaledVector(v1, a).addScaledVector(v2, b).addScaledVector(v3, c);
  };

  const triangles = [
    [vNose, vWingLeft, vSpineBack],
    [vNose, vWingRight, vSpineBack],
    [vNose, vSpineBack, vKeelBack],
  ];

  // Global Trail System
  trailGeometry = new THREE.BufferGeometry();
  const trailPositions = new Float32Array(TOTAL_TRAIL_PARTICLES * 3);
  const trailColors = new Float32Array(TOTAL_TRAIL_PARTICLES * 3);
  const trailSizes = new Float32Array(TOTAL_TRAIL_PARTICLES);
  
  for (let i = 0; i < TOTAL_TRAIL_PARTICLES; i++) {
    trailColors[i * 3] = colorPrimaryRed.r;
    trailColors[i * 3 + 1] = colorPrimaryRed.g;
    trailColors[i * 3 + 2] = colorPrimaryRed.b;
    trailSizes[i] = 0;
  }
  
  trailGeometry.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));
  trailGeometry.setAttribute('color', new THREE.BufferAttribute(trailColors, 3));
  trailGeometry.setAttribute('size', new THREE.BufferAttribute(trailSizes, 1));

  const trailMaterial = new THREE.ShaderMaterial({
    transparent: true,
    blending: THREE.AdditiveBlending,
    vertexShader: `
      attribute float size;
      varying vec3 vColor;
      void main() {
        vColor = color;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = size * (300.0 / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      void main() {
        float d = length(gl_PointCoord - vec2(0.5));
        if (d > 0.5) discard;
        gl_FragColor = vec4(vColor, (0.5 - d) * 0.8);
      }
    `,
    vertexColors: true
  });

  trailPoints = new THREE.Points(trailGeometry, trailMaterial);
  scene.add(trailPoints);

  const fleetSpeed = 1.8;
  const laneIndices = Array.from({length: PLANE_COUNT}, (_, i) => i);
  for (let i = laneIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [laneIndices[i], laneIndices[j]] = [laneIndices[j]!, laneIndices[i]!];
  }

  for (let p = 0; p < PLANE_COUNT; p++) {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLE_COUNT_PER_PLANE * 3);
    const originalPositions = new Float32Array(PARTICLE_COUNT_PER_PLANE * 3);
    const colors = new Float32Array(PARTICLE_COUNT_PER_PLANE * 3);

    // Increased range: 1.0 to 5.5
    const size = 1.0 + Math.random() * 4.5;

    for (let i = 0; i < PARTICLE_COUNT_PER_PLANE; i++) {
      const tri = triangles[Math.floor(Math.random() * triangles.length)]!;
      const pt = getRandomPointOnTriangle(tri[0]!, tri[1]!, tri[2]!);
      pt.multiplyScalar(size * 0.5);

      positions[i * 3] = pt.x;
      positions[i * 3 + 1] = pt.y;
      positions[i * 3 + 2] = pt.z;

      originalPositions[i * 3] = pt.x;
      originalPositions[i * 3 + 1] = pt.y;
      originalPositions[i * 3 + 2] = pt.z;

      // Apply Plane Fold Gradient (Sky Slate to Cloudburst)
      const ratio = (pt.z + 1.5) / 4; // Normalized depth
      const finalColor = colorSkySlate.clone().lerp(colorCloudburst, ratio);
      colors[i * 3] = finalColor.r;
      colors[i * 3 + 1] = finalColor.g;
      colors[i * 3 + 2] = finalColor.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.04,
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending
    });

    const mesh = new THREE.Points(geometry, material);
    scene.add(mesh);

    const yZoneSize = 25 / PLANE_COUNT; // Adjusted for more lanes
    const laneIndex = laneIndices[p]!;
    const yBase = -12.5 + (laneIndex * yZoneSize) + (yZoneSize / 2);

    planes.push({
      points: mesh,
      geometry: geometry,
      originalPositions: originalPositions,
      trailHistory: Array.from({length: TRAIL_POINTS_PER_PLANE}, () => new THREE.Vector3(-999, -999, -999)),
      config: {
        size,
        speedScale: fleetSpeed,
        yPos: yBase + (Math.random() - 0.5) * 3,
        zPos: (Math.random() - 0.5) * 10,
        wobbleSpeed: 0.3 + Math.random() * 0.5,
        startX: -50,
        endX: 50,
        delay: p * 0.4 + Math.random() * 0.3
      }
    });
  }

  window.addEventListener('mousemove', onMouseMove);
  window.addEventListener('touchstart', onTouchStart, { passive: false });
  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend', onTouchEnd);
  window.addEventListener('mouseleave', onTouchEnd);
  window.addEventListener('resize', onWindowResize);
  window.addEventListener('scroll', onScroll);

  animate();
});

function updateMouse(clientX: number, clientY: number) {
  if (!container.value) return;
  // Use window dimensions directly for fixed full-screen container
  const x = (clientX / window.innerWidth) * 2 - 1;
  const y = -(clientY / window.innerHeight) * 2 + 1;
  mouse.x = x;
  mouse.y = y;
}

function onMouseMove(event: MouseEvent) {
  updateMouse(event.clientX, event.clientY);
}

function onTouchStart(event: TouchEvent) {
  if (event.touches[0]) {
    updateMouse(event.touches[0].clientX, event.touches[0].clientY);
  }
}

function onTouchMove(event: TouchEvent) {
  if (event.touches[0]) {
    updateMouse(event.touches[0].clientX, event.touches[0].clientY);
  }
}

function onTouchEnd() {
  mouse.x = -9999;
  mouse.y = -9999;
}

function onWindowResize() {
  if (!camera || !renderer) return;
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
}

function onScroll() { scrollY = window.scrollY; }

function animate() {
  requestAnimationFrame(animate);
  time += 0.01;

  if (!trailGeometry || !renderer || !scene || !camera) return;

  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const rawScrollProgress = maxScroll > 0 ? scrollY / maxScroll : 0;

  const trailPosAttr = trailGeometry.attributes.position as THREE.BufferAttribute;
  const trailSizeAttr = trailGeometry.attributes.size as THREE.BufferAttribute;
  
  if (!trailPosAttr || !trailSizeAttr) return;
  
  const trailPositions: any = trailPosAttr.array;
  const trailSizes: any = trailSizeAttr.array;

  planes.forEach((pInstance, pIdx) => {
    const { points, geometry, originalPositions, config } = pInstance;
    const progress = (rawScrollProgress * (config.speedScale + config.delay)) - config.delay;
    const clampedProgress = Math.min(Math.max(progress, -0.5), 1.5);
    const currentX = config.startX + (config.endX - config.startX) * clampedProgress;
    const wobbleX = Math.sin(time * config.wobbleSpeed) * 0.3;
    const wobbleY = Math.cos(time * config.wobbleSpeed * 0.8) * 0.4;
    
    points.position.x = currentX + wobbleX;
    points.position.y = config.yPos + wobbleY;
    points.position.z = config.zPos;
    points.rotation.y = (Math.PI / 2) + Math.sin(time * 0.2) * 0.05; 
    points.rotation.z = -0.4 + Math.sin(time * config.wobbleSpeed) * 0.1;
    points.rotation.x = 0.2 + Math.cos(time * 0.3) * 0.05;

    // Trail logic
    for (let i = 0; i < TRAIL_POINTS_PER_PLANE; i++) {
      const idx = pIdx * TRAIL_POINTS_PER_PLANE + i;
      const segmentSpacing = 0.12;
      const startGap = 4.0;
      const depthOffset = -(startGap + (i * segmentSpacing));
      const waveY = Math.sin(time * 4 + i * 0.2) * 0.2 * config.size;
      const pt = new THREE.Vector3(0, waveY, depthOffset);
      pt.applyEuler(points.rotation);
      pt.add(points.position);
      trailPositions[idx * 3] = pt.x;
      trailPositions[idx * 3 + 1] = pt.y;
      trailPositions[idx * 3 + 2] = pt.z;
      const taperFactor = Math.pow(1 - (i / TRAIL_POINTS_PER_PLANE), 0.7);
      trailSizes[idx] = 0.12 * config.size * taperFactor;
    }

    // Interaction logic (Z-depth fix)
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    const depthPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -config.zPos);
    const mouseWorldPos = new THREE.Vector3();
    raycaster.ray.intersectPlane(depthPlane, mouseWorldPos);

    const worldMatrix = new THREE.Matrix4();
    worldMatrix.makeRotationFromEuler(points.rotation);
    worldMatrix.setPosition(points.position);
    const inverseMatrix = worldMatrix.invert();
    const localMouse = mouseWorldPos.clone().applyMatrix4(inverseMatrix);
    
    const posAttr = geometry.attributes.position as THREE.BufferAttribute;
    if (!posAttr) return;
    
    const positions: any = posAttr.array;
    for (let i = 0; i < PARTICLE_COUNT_PER_PLANE; i++) {
      const ix = i * 3;
      const px = positions[ix], py = positions[ix + 1], pz = positions[ix + 2];
      const interactionRadius = 6.0; // Fixed radius for all sizes
      const dist = Math.sqrt((px - localMouse.x)**2 + (py - localMouse.y)**2 + (pz - localMouse.z)**2);
      if (dist < interactionRadius) {
        const force = (interactionRadius - dist) * 0.05; // Fixed force multiplier
        positions[ix] += ((px - localMouse.x) / dist) * force;
        positions[ix + 1] += ((py - localMouse.y) / dist) * force;
        positions[ix + 2] += ((pz - localMouse.z) / dist) * force;
      } else {
        positions[ix] += (originalPositions[ix]! - positions[ix]) * 0.08;
        positions[ix + 1] += (originalPositions[ix + 1]! - positions[ix + 1]) * 0.08;
        positions[ix + 2] += (originalPositions[ix + 2]! - positions[ix + 2]) * 0.08;
      }
    }
    posAttr.needsUpdate = true;
  });

  trailPosAttr.needsUpdate = true;
  trailSizeAttr.needsUpdate = true;
  renderer.render(scene, camera);
}

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove);
  window.removeEventListener('touchstart', onTouchStart);
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend', onTouchEnd);
  window.removeEventListener('mouseleave', onTouchEnd);
  window.removeEventListener('resize', onWindowResize);
  window.removeEventListener('scroll', onScroll);
  if (renderer) renderer.dispose();
});
</script>

<template>
  <div ref="container" class="three-container"></div>
</template>

<style scoped>
.three-container {
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
}
</style>
